/**
 * useFormSchemaViews — Load detail tabs from FormSchema views section (DRR-165)
 *
 * Queries the FormSchema for the current primitive type and extracts the
 * views.detail_tabs configuration. Returns tab definitions that merge with
 * (or replace) the default hardcoded tabs from tabRegistry.ts.
 *
 * When FormSchema has no `views` section, returns null and the panel
 * falls back to default tabs.
 *
 * FERP Principles:
 * - Backend-driven: tab config from FormSchema definition (DRR-055)
 * - DRY: ONE hook, all primitives
 * - Dumb Frontend: renders what backend defines
 */

import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type { PrimitiveType } from '@/config/primitive-metadata';
import type { DetailTab, TabDefinition } from './tabRegistry';
import {
  IconEye,
  IconEdit,
  IconLink,
  IconHistory,
  IconTimeline,
  IconGitCompare,
  IconSitemap,
  IconCalendar,
  IconGitBranch,
  IconMap,
  IconFiles,
  IconNotes,
  IconArrowsExchange,
  IconTopologyFull,
  IconPlayerPlay,
  IconStack2,
  IconShield,
} from '@tabler/icons-react';

// ── GraphQL ──────────────────────────────────────────────────────────────────

const GET_FORM_SCHEMA_VIEWS = gql`
  query GetFormSchemaViews($subgraph: String!, $entityType: String!) {
    formSchema(subgraph: $subgraph, entityType: $entityType) {
      views
    }
  }
`;

// ── Icon map (icon name string → component) ─────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  IconEye,
  IconEdit,
  IconLink,
  IconHistory,
  IconTimeline,
  IconGitCompare,
  IconSitemap,
  IconCalendar,
  IconGitBranch,
  IconMap,
  IconFiles,
  IconNotes,
  IconArrowsExchange,
  IconTopologyFull,
  IconPlayerPlay,
  IconStack2,
  IconShield,
};

// ── Types ────────────────────────────────────────────────────────────────────

interface DetailTabConfig {
  id: string;
  label: string;
  icon: string;
  renderer: string;
  order: number;
  props?: Record<string, unknown>;
}

interface FormSchemaViewsResult {
  /** Tab definitions from FormSchema (null if no views section) */
  tabs: TabDefinition[] | null;
  /** Raw tab configs with renderer keys (for TabRendererRegistry lookup) */
  tabConfigs: DetailTabConfig[] | null;
  /** Whether the query is still loading */
  loading: boolean;
}

// ── Primitive type → entity type mapping ─────────────────────────────────────

function primitiveToEntityType(primitiveType: PrimitiveType): string {
  // PascalCase: 'instance' → 'Instance', 'workflow' → 'Workflow'
  return primitiveType.charAt(0).toUpperCase() + primitiveType.slice(1);
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useFormSchemaViews(primitiveType: PrimitiveType): FormSchemaViewsResult {
  const entityType = primitiveToEntityType(primitiveType);

  const { data, loading } = useQuery<{
    formSchema: { views: Record<string, unknown> | null } | null;
  }>(GET_FORM_SCHEMA_VIEWS, {
    variables: { subgraph: 'core', entityType },
    fetchPolicy: 'cache-first',
  });

  const result = useMemo((): { tabs: TabDefinition[] | null; tabConfigs: DetailTabConfig[] | null } => {
    const views = data?.formSchema?.views;
    if (!views) return { tabs: null, tabConfigs: null };

    const detailTabs = (views as Record<string, unknown>).detail_tabs;
    if (!Array.isArray(detailTabs) || detailTabs.length === 0) return { tabs: null, tabConfigs: null };

    const configs = detailTabs as DetailTabConfig[];
    const sorted = [...configs].sort((a, b) => a.order - b.order);

    const tabDefs: TabDefinition[] = sorted.map((cfg) => ({
      id: cfg.id as DetailTab,
      label: cfg.label,
      icon: ICON_MAP[cfg.icon] ?? IconEye,
    }));

    return { tabs: tabDefs, tabConfigs: sorted };
  }, [data]);

  return {
    tabs: result.tabs,
    tabConfigs: result.tabConfigs,
    loading,
  };
}
